"use client";
import { useState, useCallback } from "react";

export type Mentor = {
  id: string;
  userId?: string;
  displayName?: string;
  avatar?: string | null;
  avatarUrl?: string | null;
  bio?: string | null;
  countriesOfExpertise?: string[] | null;
  fieldsOfStudy?: string[] | null;
  rating?: number | null;
  totalReviews?: number | null;
  totalArticles?: number | null;
  totalPoints?: number | null;
  isAvailableForHire?: boolean | null;
  hourlyRate?: number | null;
  earnedBadges?: unknown[] | null;
};

export type MentorFilters = {
  countries: string[];
  fields: string[];
  minRating: number;
  onlyAvailable: boolean;
  maxHourlyRate: number | null; // null = no cap
  hideMentorsWithoutRate: boolean;
  minReviews: number;
};

export const defaultFilters: MentorFilters = {
  countries: [],
  fields: [],
  minRating: 0,
  onlyAvailable: false,
  maxHourlyRate: null,
  hideMentorsWithoutRate: false,
  minReviews: 0,
};

export function activeFilterCount(f: MentorFilters, maxCap: number): number {
  let n = 0;
  if (f.countries.length > 0) n += 1;
  if (f.fields.length > 0) n += 1;
  if (f.minRating > 0) n += 1;
  if (f.onlyAvailable) n += 1;
  if (f.maxHourlyRate !== null && f.maxHourlyRate < maxCap) n += 1;
  if (f.hideMentorsWithoutRate) n += 1;
  if (f.minReviews > 0) n += 1;
  return n;
}

export function useMentorFilters(initial: MentorFilters = defaultFilters) {
  const [filters, setFiltersState] = useState<MentorFilters>(initial);

  const setFilters = useCallback(
    (update: Partial<MentorFilters> | ((prev: MentorFilters) => MentorFilters)) => {
      setFiltersState((prev) =>
        typeof update === "function" ? update(prev) : { ...prev, ...update }
      );
    },
    []
  );

  const clear = useCallback(() => {
    setFiltersState(defaultFilters);
  }, []);

  const apply = useCallback(
    (mentors: Mentor[] | undefined): Mentor[] => {
      if (!mentors) return [];
      return mentors.filter((m) => {
        // Countries — mentor passes if at least one of mentor's countries is in selected list.
        if (filters.countries.length > 0) {
          const coe = Array.isArray(m.countriesOfExpertise) ? m.countriesOfExpertise : [];
          if (!coe.some((c) => filters.countries.includes(c))) return false;
        }

        // Fields — same logic
        if (filters.fields.length > 0) {
          const fos = Array.isArray(m.fieldsOfStudy) ? m.fieldsOfStudy : [];
          if (!fos.some((f) => filters.fields.includes(f))) return false;
        }

        // Rating floor
        if ((m.rating ?? 0) < filters.minRating) return false;

        // Availability toggle
        if (filters.onlyAvailable && !m.isAvailableForHire) return false;

        // Hourly rate cap — mentors without a rate pass unless hideMentorsWithoutRate on.
        if (m.hourlyRate == null) {
          if (filters.hideMentorsWithoutRate) return false;
        } else if (filters.maxHourlyRate !== null && m.hourlyRate > filters.maxHourlyRate) {
          return false;
        }

        // Min reviews
        if ((m.totalReviews ?? 0) < filters.minReviews) return false;

        return true;
      });
    },
    [filters]
  );

  return { filters, setFilters, apply, clear };
}
